import { supabase } from '@/integrations/supabase/client';

/**
 * Shared loader for lens JSON_PROPERTIES rows.
 *
 * Header and page content frequently request the same visible lens-id set during
 * the same mount, producing duplicate identical roundtrips. This helper coalesces
 * only IN-FLIGHT identical requests. It is NOT a cache: the entry is removed as
 * soon as the promise settles, so every new call after settlement hits Supabase
 * again and freshness semantics are unchanged.
 */

export const LENS_JSON_PROPERTIES_ATTRIBUTE_NAME = 'JSON_PROPERTIES';
export const LENS_JSON_PROPERTIES_PROJECTION = 'thinking_method_id, attribute_value';

export type LensJsonPropertiesRow = {
  thinking_method_id: string;
  attribute_value: string;
};

export type LensJsonPropertiesResult = {
  data: LensJsonPropertiesRow[] | null;
  error: unknown;
};

const inFlight = new Map<string, Promise<LensJsonPropertiesResult>>();

const canonicalizeIds = (methodIds: string[]): string[] =>
  Array.from(new Set(methodIds)).sort();

export const buildLensJsonPropertiesRequestKey = (methodIds: string[]): string =>
  canonicalizeIds(methodIds).join(',');

export async function loadLensJsonProperties(
  methodIds: string[],
): Promise<LensJsonPropertiesResult> {
  const ids = canonicalizeIds(methodIds);

  if (ids.length === 0) {
    return { data: [], error: null };
  }

  const key = ids.join(',');
  const existing = inFlight.get(key);
  if (existing) return existing;

  const request = (async (): Promise<LensJsonPropertiesResult> => {
    const { data, error } = await supabase
      .from('thinking_method_attributes')
      .select(LENS_JSON_PROPERTIES_PROJECTION)
      .in('thinking_method_id', ids)
      .eq('attribute_name', LENS_JSON_PROPERTIES_ATTRIBUTE_NAME);

    return { data: (data ?? null) as LensJsonPropertiesRow[] | null, error };
  })().finally(() => {
    inFlight.delete(key);
  });

  inFlight.set(key, request);
  return request;
}
